/**
 * KillStreakToast — Bottom-center DOM toast announcing kill streaks
 * (DOUBLE / TRIPLE / QUAD / PENTA KILL!). Each `show(level)` spawns a
 * fresh toast that auto-disposes after `TOAST_LIFETIME_MS`.
 *
 * Pure DOM (no WebGL) so it stays unit-testable in the Node test env.
 * The container is a thin wrapper appended to `parentElement`; toasts
 * are children of that container so `dispose()` cleans up cleanly.
 */

export const TOAST_LIFETIME_MS = 1500;

export const KILL_STREAK_LABELS: Record<number, string> = {
  2: 'DOUBLE KILL!',
  3: 'TRIPLE KILL!',
  4: 'QUAD KILL!',
  5: 'PENTA KILL!',
};

/**
 * Resolve the human-readable label for a kill-streak level. Levels of 5
 * or higher all roll up to "PENTA KILL!" (the canonical max tier). Levels
 * less than 2 return null — a single kill is not a streak.
 */
export function killStreakLabel(level: number): string | null {
  if (!Number.isFinite(level) || level < 2) return null;
  if (level >= 5) return KILL_STREAK_LABELS[5];
  return KILL_STREAK_LABELS[level] ?? null;
}

export class KillStreakToast {
  private container: HTMLElement;
  // Per-toast timers so dispose() can cancel pending removals (HMR leak
  // guard) and any re-schedule of the same element clears the prior timer
  // before adding a new one (avoids double-removal).
  private timers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();

  constructor(parentElement: HTMLElement) {
    this.container = document.createElement('div');
    this.container.className = 'kill-streak-toast-layer';
    parentElement.appendChild(this.container);
  }

  /**
   * Show a kill-streak toast for the given streak level. Level 4+ gets a
   * golden gradient (`--gold` modifier). Auto-removes after TOAST_LIFETIME_MS.
   *
   * Returns the spawned toast element so callers (and tests) can inspect
   * it. Returns null when the level is out of range (< 2).
   */
  show(level: number): HTMLElement | null {
    const label = killStreakLabel(level);
    if (label === null) return null;

    const toast = document.createElement('div');
    toast.className = 'kill-streak-toast';
    if (level >= 4) toast.classList.add('kill-streak-toast--gold');
    toast.textContent = label;

    this.container.appendChild(toast);

    // Cancel any stale timer for this exact element before registering a new one.
    const prior = this.timers.get(toast);
    if (prior !== undefined) clearTimeout(prior);

    const timer = setTimeout(() => {
      this.timers.delete(toast);
      // Only remove if still attached — dispose() or a prior removal may have detached us.
      if (toast.parentElement) toast.parentElement.removeChild(toast);
      else if (typeof (toast as unknown as { remove?: () => void }).remove === 'function') {
        (toast as unknown as { remove: () => void }).remove();
      }
    }, TOAST_LIFETIME_MS);
    this.timers.set(toast, timer);

    return toast;
  }

  dispose(): void {
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
    if (this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
  }
}
